import axios from "axios";
import { API_URL } from "../config";
import { user } from "./actions_helper";

export type AiRunStatus = "queued" | "running" | "succeeded" | "failed";

export interface IAiRunSkipped {
    nr?: string | null;
    short_name?: string | null;
    reason: string;
}

export interface IAiRunFailedPage {
    page: number;
    error: string;
}

export interface IOfferImportPosition {
    nr: string | null;
    title: string;
    qty: number;
    unit_price_cents: number;
    total_cents: number | null;
}

export interface IAiRun {
    id: number;
    kind: string;
    status: AiRunStatus;
    model: string | null;
    input: {
        inventory_type?: string;
        filename?: string;
        mime?: string;
        size?: number;
    };
    output: {
        pages_total?: number;
        pages_done?: number;
        created_ids?: number[];
        skipped?: IAiRunSkipped[];
        failed_pages?: IAiRunFailedPage[];
        supplier?: string | null;
        offer_number?: string | null;
        positions?: IOfferImportPosition[];
        positions_total_cents?: number;
        net_total_cents?: number | null;
        sum_matches?: boolean | null;
        truncated_pages?: number;
    };
    error: string | null;
    usage: Record<string, number> | null;
    items_total: number;
    items_unapproved: number;
    created_at: string;
    updated_at: string;
}

const authHeaders = () => ({ headers: { authorization: user().token } });

export const isAiRunFinished = (run: IAiRun | null | undefined): boolean =>
    !!run && (run.status === "succeeded" || run.status === "failed");

export async function createInventoryImport(file: File, inventoryType: string): Promise<IAiRun> {
    const formData = new FormData();
    formData.append("kind", "inventory_import");
    formData.append("inventory_type", inventoryType);
    formData.append("file", file);
    const response = await axios.post(`${API_URL}/ai_runs.json`, formData, {
        headers: { ...authHeaders().headers, "Content-Type": "multipart/form-data" },
    });
    return response.data;
}

export async function createOfferImport(file: File): Promise<IAiRun> {
    const formData = new FormData();
    formData.append("kind", "offer_import");
    formData.append("file", file);
    const response = await axios.post(`${API_URL}/ai_runs.json`, formData, {
        headers: { ...authHeaders().headers, "Content-Type": "multipart/form-data" },
    });
    return response.data;
}

export async function getAiRun(id: number | string): Promise<IAiRun> {
    const response = await axios.get(`${API_URL}/ai_runs/${id}.json`, authHeaders());
    return response.data;
}

export async function approveAiRunItems(id: number | string): Promise<{ approved: number }> {
    const response = await axios.patch(
        `${API_URL}/ai_runs/${id}/approve_items.json`,
        {},
        authHeaders()
    );
    return response.data;
}

export async function discardAiRunItems(id: number | string): Promise<{ discarded: number }> {
    const response = await axios.delete(
        `${API_URL}/ai_runs/${id}/discard_items.json`,
        authHeaders()
    );
    return response.data;
}

export const aiErrorMessage = (err: any, fallback = "KI-Aktion fehlgeschlagen"): string => {
    const serverMessage = err?.response?.data?.errors?.[0] ?? err?.response?.data?.error;
    if (serverMessage) {
        return serverMessage;
    }
    if (err?.response?.status === 502) {
        return "KI-Dienst nicht erreichbar";
    }
    return fallback;
};
